
import { useEffect, useMemo, useState, useCallback } from 'react';
import {
    Box,
    Flex,
    Heading,
    Text,
    Button,
    SimpleGrid,
    IconButton,
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { isEmpty, isNil, omitBy } from 'lodash';

import { MobileDashboardSection } from './components/DashboardStyles';
import SearchFilterInput from 'components/Dashboard/SearchFilterInput';
import ProductItemNew, { ProductCardSkeleton } from 'components/Dashboard/ProductItemNew';
import FilterComponent from 'components/Products/FilterComponent';
import ApiPagination from 'components/Dashboard/ApiPagination';
import AddToCartModal from './components/AddToCartModal';
import { get_all_products } from '../../redux/actions/productActions';
import useDebounce from 'hooks/useDebounce';
import { ITEMS_PER_PAGE } from 'utils/utils';

/* ─────────────────────────────────────────────────────────────
   BRAND TOKENS — matches the rest of the refactored app
   ───────────────────────────────────────────────────────────── */

const NAVY        = '#1B3B5F';
const NAVY_DEEP   = '#0F2940';
const CYAN        = '#DCF8F9';
const INK         = '#1A2740';
const MUTED       = '#5A6B7E';
const FAINT       = '#8896AE';
const SURFACE     = '#FFFFFF';
const SURFACE_ALT = '#F7F9FB';
const BORDER      = '#E8EDF5';

const FONT = "'Bricolage Grotesque', sans-serif";

const SHADOW_SM = '0 1px 3px rgba(27,59,95,0.06)';

/* ─────────────────────────────────────────────────────────────
   ICONS
   ───────────────────────────────────────────────────────────── */

const InboxIcon = (props) => (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M22 12h-6l-2 3h-4l-2-3H2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M5.5 5h13l3 7v6a2 2 0 01-2 2h-15a2 2 0 01-2-2v-6l3-7z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
);

const CloseIcon = (props) => (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
);
const BackArrowIcon = (props) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M19 12H5M12 19l-7-7 7-7"
            stroke="currentColor" strokeWidth="2.2"
            strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

/* ─────────────────────────────────────────────────────────────
   ACTIVE FILTER PILL — small dismissible chip
   ───────────────────────────────────────────────────────────── */

const ActiveFilterPill = ({ label, value, onClear }) => (
    <Flex
        align="center"
        gap="6px"
        bg={CYAN}
        color={NAVY}
        fontSize="11px"
        fontWeight="700"
        fontFamily={FONT}
        px="10px"
        py="4px"
        borderRadius="full"
        border="1px solid"
        borderColor={`${NAVY}20`}
    >
        <Text as="span" color={MUTED} fontWeight="600">
            {label}:
        </Text>
        <Text as="span" noOfLines={1} maxW="120px">
            {value}
        </Text>
        <IconButton
            aria-label={`Clear ${label} filter`}
            icon={<CloseIcon />}
            onClick={onClear}
            size="xs"
            h="16px"
            minW="16px"
            w="16px"
            borderRadius="full"
            bg="transparent"
            color={NAVY}
            _hover={{ bg: 'whiteAlpha.700' }}
            _active={{ bg: 'whiteAlpha.900' }}
        />
    </Flex>
);

/* ─────────────────────────────────────────────────────────────
   EMPTY STATE
   ───────────────────────────────────────────────────────────── */

const EmptyState = ({ hasFilters, onClearAll, searchTerm }) => (
    <Flex
        direction="column"
        align="center"
        justify="center"
        py={{ base: '40px', md: '60px' }}
        px="20px"
        textAlign="center"
        gap="12px"
        bg={SURFACE}
        border="1px solid"
        borderColor={BORDER}
        borderRadius="14px"
        boxShadow={SHADOW_SM}
        mt="20px"
    >
        <Flex
            w="64px"
            h="64px"
            borderRadius="full"
            bg={SURFACE_ALT}
            color={FAINT}
            align="center"
            justify="center"
        >
            <InboxIcon />
        </Flex>
        <Box>
            <Heading
                as="h2"
                fontSize="16px"
                fontWeight="800"
                color={INK}
                fontFamily={FONT}
                mb="4px"
            >
                {hasFilters || searchTerm ? 'No products match your filters' : 'No products yet'}
            </Heading>
            <Text fontSize="12px" color={MUTED} maxW="320px">
                {hasFilters || searchTerm
                    ? 'Try adjusting your filters or search to see more results.'
                    : 'Check back soon — fresh stock arrives daily.'}
            </Text>
        </Box>
        {(hasFilters || searchTerm) && (
            <Button
                onClick={onClearAll}
                bg={NAVY}
                color="white"
                _hover={{ bg: NAVY_DEEP }}
                _active={{ bg: NAVY_DEEP }}
                size="sm"
                fontSize="12px"
                fontWeight="700"
                fontFamily={FONT}
                borderRadius="8px"
                h="36px"
                px="16px"
                mt="4px"
            >
                Clear all filters
            </Button>
        )}
    </Flex>
);

/* ─────────────────────────────────────────────────────────────
   SKELETON GRID — shown during initial load + pagination
   ───────────────────────────────────────────────────────────── */

const SkeletonGrid = ({ count = 12 }) => (
    <SimpleGrid
        columns={{ base: 2, md: 3, lg: 4, xl: 5, '2xl': 6 }}
        spacing={{ base: '8px', md: '14px' }}
    >
        {Array.from({ length: count }).map((_, i) => (
            <ProductCardSkeleton key={i} />
        ))}
    </SimpleGrid>
);

/* ─────────────────────────────────────────────────────────────
   FILTER HELPERS
   ───────────────────────────────────────────────────────────── */

/* Strip undefined/null/empty values from filters before
   sending to the API. Avoids `{ brand: undefined }` payloads. */
const cleanFilters = (filters) => {
    return omitBy(
        filters,
        (v) => isNil(v) || v === '' || v === 0 || (Array.isArray(v) && v.length === 0)
    );
};

/* ─────────────────────────────────────────────────────────────
   MAIN
   ───────────────────────────────────────────────────────────── */

const Products = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [searchParams, setSearchParams] = useSearchParams();

    const { products, loading } = useSelector((state) => state.productReducer);

    /* ── State ──────────────────────────────────────────────── */
    const [page, setPage] = useState(0);
    const [filters, setFilters] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedValue = useDebounce(searchTerm, 700);

    /* AddToCartModal state */
    const [isCartModalOpen, setIsCartModalOpen] = useState(false);
    const [currentItem, setCurrentItem] = useState({});
    const closeCartModal = useCallback(() => setIsCartModalOpen(false), []);

    /* ── Hydrate from URL params on mount ───────────────────── */
    /* Supports incoming links from anywhere in the app:
         /dashboard/products?q=adidas           → search "adidas"
         /dashboard/products?vendor=Stellas     → filter vendor "Stellas"
         /dashboard/products?vendor=true        → just opens products (no specific vendor)
         /dashboard/products?filter=new_arrivals → reserved for section filtering */
    useEffect(() => {
        const qParam      = searchParams.get('q');
        const vendorParam = searchParams.get('vendor');
        const brandParam  = searchParams.get('brand');

        if (qParam) {
            setSearchTerm(qParam);
        }

        const initialFilters = {};
        if (vendorParam && vendorParam !== 'true') {
            initialFilters.vendor_name = vendorParam;
        }
        if (brandParam) {
            initialFilters.brand = brandParam;
        }

        if (!isEmpty(initialFilters)) {
            setFilters(initialFilters);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); /* only on mount — subsequent navigation handled by user actions */

    /* ── Fetch products when any input changes ──────────────── */
    useEffect(() => {
        const payload = {
            per_page: ITEMS_PER_PAGE,
            page:     page + 1,
            ...(debouncedValue ? { name: debouncedValue } : {}),
            ...cleanFilters(filters),
        };
        dispatch(get_all_products(payload));
    }, [dispatch, page, debouncedValue, filters]);

    /* Reset to page 0 when search or filters change */
    useEffect(() => {
        if (debouncedValue || !isEmpty(cleanFilters(filters))) {
            setPage(0);
        }
    }, [debouncedValue, filters]);

    /* ── Derived values ─────────────────────────────────────── */

    const items = products?.items ?? [];
    const totalCount = products?.page_context?.total ?? items.length;

    /* Active filter values for the pill row */
    const activeFilters = useMemo(() => {
        const list = [];
        if (filters.vendor_name) {
            list.push({ key: 'vendor_name', label: 'Vendor', value: filters.vendor_name });
        }
        if (filters.brand) {
            list.push({ key: 'brand', label: 'Brand', value: filters.brand });
        }
        if (filters.price_sort > 0) {
            list.push({
                key: 'price_sort',
                label: 'Max price',
                value: `₦${Number(filters.price_sort).toLocaleString()}`,
            });
        }
        if (filters.stock_on_hand) {
            list.push({ key: 'stock_on_hand', label: 'Stock', value: filters.stock_on_hand });
        }
        return list;
    }, [filters]);

    const hasFilters = activeFilters.length > 0;
    const hasResults = items.length > 0;
    const showSkeleton = loading && !hasResults;
    const showPaginationLoader = loading && hasResults;

    /* ── Handlers ───────────────────────────────────────────── */

    const handleSearch = useCallback((value) => {
        setSearchTerm(value);
        /* Keep URL in sync — but don't push to history (avoid back-button spam) */
        const next = new URLSearchParams(searchParams);
        if (value) next.set('q', value);
        else next.delete('q');
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);

    const handleFilter = useCallback((values) => {
        const newFilters = {
            price_sort:    values.sliderValue,
            vendor_name:   values.selectedVendors?.[0],
            brand:         values.selectedBrands?.[0],
            stock_on_hand: values.stock_on_hand,
        };
        setFilters(newFilters);

        /* Sync vendor/brand to URL so the state survives a refresh */
        const next = new URLSearchParams(searchParams);
        if (newFilters.vendor_name) next.set('vendor', newFilters.vendor_name);
        else next.delete('vendor');
        if (newFilters.brand) next.set('brand', newFilters.brand);
        else next.delete('brand');
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);

    const handleClearFilter = useCallback((key) => {
        setFilters((prev) => {
            const next = { ...prev };
            delete next[key];
            return next;
        });
        const next = new URLSearchParams(searchParams);
        if (key === 'vendor_name') next.delete('vendor');
        if (key === 'brand') next.delete('brand');
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);

    const handleClearAll = useCallback(() => {
        setSearchTerm('');
        setFilters({});
        setSearchParams({}, { replace: true });
    }, [setSearchParams]);

    const handleAddToCart = useCallback((item) => {
        setCurrentItem(item);
        setIsCartModalOpen(true);
    }, []);

    /* ── Render ─────────────────────────────────────────────── */

    return (
        <MobileDashboardSection>
            <Box
                px={{ base: '12px', md: '16px', lg: '0' }}
                maxW="1400px"
                mx="auto"
                w="full"
                fontFamily={FONT}
            >
                {/* ── HEADER ── */}
               <Flex
    align={{ base: 'flex-start', md: 'center' }}
    justify="space-between"
    direction={{ base: 'column', md: 'row' }}
    gap="6px"
    mb={{ base: '14px', md: '20px' }}
>
    <Flex align="center" gap="10px">
        <IconButton
            aria-label="Go back"
            icon={<BackArrowIcon />}
            onClick={() => navigate(-1)}
            variant="ghost"
            color={MUTED}
            h="36px"
            minW="36px"
            w="36px"
            borderRadius="8px"
            _hover={{ bg: SURFACE_ALT, color: NAVY }}
            _active={{ bg: BORDER }}
        />
        <Box>
            <Heading
                as="h1"
                fontSize={{ base: '22px', md: '26px' }}
                fontWeight="800"
                color={INK}
                fontFamily={FONT}
                letterSpacing="-0.02em"
                lineHeight="1.1"
                mb="2px"
            >
                All Products
            </Heading>
            <Text fontSize="12px" color={MUTED}>
                {loading && !hasResults
                    ? 'Loading products…'
                    : totalCount > 0
                    ? `${totalCount.toLocaleString()} ${totalCount === 1 ? 'product' : 'products'} available`
                    : 'No products available'}
            </Text>
        </Box>
    </Flex>
</Flex>

                {/* ── SEARCH + FILTER BAR ── */}
                <Box mb="12px">
                    <SearchFilterInput
                        showFilter
                        source="all products"
                        value={searchTerm}
                        onSearch={handleSearch}
                        handleFilter={handleFilter}
                        FilterButton={FilterComponent}
                    />
                </Box>

                {/* ── ACTIVE FILTER PILLS ── */}
                {(hasFilters || searchTerm) && (
                    <Flex
                        align="center"
                        gap="8px"
                        flexWrap="wrap"
                        mb="16px"
                        pb="12px"
                        borderBottom="1px solid"
                        borderColor={BORDER}
                    >
                        <Text fontSize="11px" color={FAINT} fontWeight="700" letterSpacing="0.08em">
                            ACTIVE:
                        </Text>
                        {searchTerm && (
                            <ActiveFilterPill
                                label="Search"
                                value={`"${searchTerm}"`}
                                onClear={() => handleSearch('')}
                            />
                        )}
                        {activeFilters.map((f) => (
                            <ActiveFilterPill
                                key={f.key}
                                label={f.label}
                                value={f.value}
                                onClear={() => handleClearFilter(f.key)}
                            />
                        ))}
                        <Button
                            onClick={handleClearAll}
                            size="xs"
                            variant="ghost"
                            color={MUTED}
                            fontSize="11px"
                            fontWeight="700"
                            _hover={{ color: NAVY, bg: 'transparent', textDecoration: 'underline' }}
                            h="24px"
                            px="6px"
                        >
                            Clear all
                        </Button>
                    </Flex>
                )}

                {/* ── BODY ── */}
                {showSkeleton ? (
                    <SkeletonGrid count={12} />
                ) : !hasResults ? (
                    <EmptyState
                        hasFilters={hasFilters}
                        searchTerm={searchTerm}
                        onClearAll={handleClearAll}
                    />
                ) : (
                    <>
                        <Box position="relative">
                            <SimpleGrid
                                columns={{ base: 2, md: 3, lg: 4, xl: 5, '2xl': 6 }}
                                spacing={{ base: '8px', md: '14px' }}
                                opacity={showPaginationLoader ? 0.5 : 1}
                                transition="opacity 0.2s ease"
                            >
                                {items.map((item) => (
                                    <ProductItemNew
                                        key={item.item_id}
                                        item={item}
                                        onAddToCart={handleAddToCart}
                                    />
                                ))}
                            </SimpleGrid>

                            {/* Subtle overlay during pagination — keeps grid visible */}
                            {showPaginationLoader && (
                                <Box
                                    position="absolute"
                                    inset="0"
                                    pointerEvents="none"
                                />
                            )}
                        </Box>

                        <Box mt="24px">
                            <ApiPagination
                                page={page}
                                setPage={setPage}
                                hasMorePages={products?.page_context?.has_more_page}
                            />
                        </Box>
                    </>
                )}
            </Box>

            <AddToCartModal
                isModalOpen={isCartModalOpen}
                closeModal={closeCartModal}
                setIsModalOpen={setIsCartModalOpen}
                currentItem={currentItem}
            />
        </MobileDashboardSection>
    );
};

export default Products;